/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { Dimensions } from "@dojo/framework/core/meta/Dimensions";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import * as _ from "lodash";
import { PresetShapeInjector } from "../../store/PresetShapeInjector";
import { SelectionContainer } from "../containers/ShapeContainer";
import { IApiSet } from "../injectors/ApiContext";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { GeometryTypes, IPageObjectTypes } from "../interfaces/model/enumerations/index";
import { CanvasMode } from "../interfaces/model/enumerations/CanvasMode";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { ICanvasEditor, IObjectSelection, IPage } from "../interfaces/model/index";
import { IBrowserType } from "../interfaces/model/index";
import { IGroupShape } from "../interfaces/model/PageObjects/index";
import { IShape } from "../interfaces/model/PageObjects/Shape";
import { getCustomGeometry, getGeometry } from "../selectors/index";
import { EditCurvePoints } from "./EditCurvePoints";
import { EditPenPoints } from "./EditPenPoints";
import { PictureEditCropBox } from "./PictureEditCropBox";
import { PictureCustomEditCropBox } from "./PictureCustomEditCropBox";
import { getIndexedChildren } from "../../store/reducers/util";

/**
 * Selection overlay  interface
 */
export interface ISelectionOverlay extends IObjectSelection {

    /** Page */
    page: IPage;

    /**
     * Canvas editor interface object
     */
    canvasEditor: ICanvasEditor;

    /**
     * Selected item within group index
     */
    groupSelectionIndices: number[];

    /**
     * Api set
     */
    api: IApiSet;

    /**
     * Current canvas mode.
     */
    canvasMode: CanvasMode;

    /**
     * Selected curve point
     */
    selectedCurvePoint: number | undefined;

    /**
     * Zoom factor value
     */
    zoomFactor: number;

    /**
     * Borwser type on which the app is running
     */
    browserType: IBrowserType;
}

/**
 * Canvas Objects selection.
 * Provides base component for rendering object selection
 */
export class SelectionOverlay extends WidgetBase<ISelectionOverlay> {
	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		const { canvasEditor, groupSelectionIndices, api, selectedCurvePoint, browserType } = this.properties;
		let { selectedObjects } = this.properties;
		const { editPageObject, setCanvasMode, setSelectedCurvePoint } = api.canvasApi;
		const { deleteCurvePoint, rotateObjects, scaleObjects, setShapeAutosizeValues, setCurvePoints, setHandlePoints, translateObjects, setShapeAdjustValues } = api.pageApi;
		const { selectObjectById, setAllAspectRatios } = api.selectionApi;
		const { canvasContainerEl } = api;
		const children: DNode[] = [];
		let key: number = 0;
		const dimensions = this.meta(Dimensions)
			.get("selectionOverlay");

		if (this.properties.canvasMode === CanvasMode.EDIT_CURVE && selectedObjects && selectedObjects.length === 1
            && (groupSelectionIndices === undefined || groupSelectionIndices.length < 2)) {
			let pageObject = selectedObjects[0];
			let groupObject = undefined;
			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				groupObject = pageObject as IGroupShape;
				pageObject = groupObject.childObjects[groupSelectionIndices[0]];
			}
			children.push(
				<EditCurvePoints
					browserType={browserType}
					groupObject={groupObject}
					key={key}
					pageObject={pageObject as IShape}
					parentSize={dimensions.position}
					scalePoints
					selectedCurvePoint={selectedCurvePoint}
					setCurvePoints={setCurvePoints}
					setSelectedCurvePoint={setSelectedCurvePoint}
				/>,
			);
			key++;
		} else if (this.properties.canvasMode === CanvasMode.EDIT_PEN && selectedObjects && selectedObjects.length === 1
            && (groupSelectionIndices === undefined || groupSelectionIndices.length < 2)) {
			let pageObject = selectedObjects[0];
			let groupObject = undefined;
			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				groupObject = pageObject as IGroupShape;
				pageObject = groupObject.childObjects[groupSelectionIndices[0]];
			}
			children.push(
				<EditPenPoints
					browserType={browserType}
					canvasContainerEl={api.canvasContainerEl}
					deleteCurvePoint={deleteCurvePoint}
					groupObject={groupObject}
					key={key}
					pageObject={pageObject as IShape}
					parentSize={dimensions.position}
					selectObjectById={selectObjectById}
					selectedCurvePoint={selectedCurvePoint}
					setCanvasMode={setCanvasMode}
					setCurvePoints={setCurvePoints}
					setHandlePoints={setHandlePoints}
					setSelectedCurvePoint={setSelectedCurvePoint}
				/>,
			);
			key++;
		} else if (this.properties.canvasMode === CanvasMode.EDIT_PICTURE && selectedObjects &&
            selectedObjects.length === 1 && (groupSelectionIndices === undefined || groupSelectionIndices.length <= 1) ) {
			// This code to get geometry boundary meta for target object resides here as for PictureEditCropBox we do
			// not have corresponding container (like shape container).
			// This code also sets an example about how widgets have implicit access to all registries and how we can
			// use them without containers
			let pageObject = selectedObjects[0];
			const poIndex = _.findIndex(this.properties.page.pageObjects, pageObject);
			let groupObject = undefined;
			if (pageObject.type == IPageObjectTypes.GROUP_SHAPE && groupSelectionIndices.length > 0) {
				groupObject = pageObject as IGroupShape;
				pageObject = groupObject.childObjects[groupSelectionIndices[0]];
			}
			let geoBoundaryMeta: IGeoBoundaryMeta | undefined = undefined;
			if (pageObject.type === IPageObjectTypes.SHAPE) {
				if (pageObject.spPr.geometry && pageObject.spPr.geometry.type === GeometryTypes.CUSTOM) {
					const geometry = pageObject.spPr.geometry;
					const { cx, cy } = pageObject.spPr.xfrm.ext;
					const geometryParams =
                        getCustomGeometry({ geometry, shapeAvList: pageObject.spPr.geometry.avList, cy, cx });
					if (geometryParams.pathInfo) {
						geoBoundaryMeta = geometryParams.pathInfo.geoBoundaryMeta;
					}

					children.push(
						<PictureCustomEditCropBox
							api={api}
							browserType={browserType}
							dimensions={dimensions}
							editPageObject={editPageObject}
							geoBoundaryMeta={geoBoundaryMeta}
							groupObject={groupObject}
							groupSelectionIndices={groupSelectionIndices}
							key={key}
							pageObject={pageObject as IShape}
							poIndex={poIndex}
							scaleObjects={scaleObjects}
							selectedCurvePoint={selectedCurvePoint}
							setAllAspectRatios={setAllAspectRatios}
							setShapeAdjustValues={setShapeAdjustValues}
							translateObjects={translateObjects}
							zoomFactor={this.properties.zoomFactor}
						/>,
					);
				} else {
					const injector = this.registry.getInjector<PresetShapeInjector>("preset-shapes");
					const inject = injector && injector.injector();
					if (pageObject.spPr.geometry && inject) {
						if (pageObject.spPr.geometry.type === GeometryTypes.PRESET) {
							if (pageObject.spPr.geometry.presetShape) {
								const ps: any = pageObject.spPr.geometry.presetShape;
								const geometry = inject.presetGeometries[ps] as IPresetGeometry;
								const { cx, cy } = pageObject.spPr.xfrm.ext;
								const geometryParams =
                                    getGeometry({ geometry, shapeAvList: pageObject.spPr.geometry.avList, cy, cx });
								if (geometryParams.pathInfo) {
									geoBoundaryMeta = geometryParams.pathInfo.geoBoundaryMeta;
								}
							}
						}
					}
					children.push(
						<PictureEditCropBox
							canvasContainerEl={canvasContainerEl}
							editPageObject={editPageObject}
							geoBoundaryMeta={geoBoundaryMeta}
							groupObject={groupObject}
							groupSelectionIndices={groupSelectionIndices}
							key={key}
							pageObject={pageObject as IShape}
							poIndex={poIndex}
							scaleObjects={scaleObjects}
							setAllAspectRatios={setAllAspectRatios}
							setShapeAdjustValues={setShapeAdjustValues}
							translateObjects={translateObjects}
							zoomFactor={this.properties.zoomFactor}
						/>,
					);
				}
			}
			key++;
		} else if (selectedObjects && selectedObjects.length) {
			// If mode is EDIT_CURVE and there are more than 1 selected objects then fall back to SELECT mode.
			let groupObject = undefined;
			if (groupSelectionIndices &&
                groupSelectionIndices.length > 0) {
				groupObject = selectedObjects[0];
				selectedObjects = getIndexedChildren((selectedObjects[0] as IGroupShape).childObjects, groupSelectionIndices);
			}
			for (const pageObject of selectedObjects) {
				children.push(
					<SelectionContainer
						canvasContainerEl={canvasContainerEl}
						groupObject={groupObject}
						isCanvasTemplate={canvasEditor.isCanvasTemplate}
						isMultiSelect={selectedObjects.length > 1}
						isPanning={canvasEditor.isPanning}
						isTempShape={false}
						key={key}
						pageObject={pageObject}
						rotateObjects={rotateObjects}
						scaleObjects={scaleObjects}
						selectObjectById={selectObjectById}
						setAllAspectRatios={setAllAspectRatios}
						setCanvasMode={setCanvasMode}
						setShapeAdjustValues={setShapeAdjustValues}
						setShapeAutosizeValues={setShapeAutosizeValues}
					/>,
				);
				key++;
			}
		}
		return (
			<div
				classes={["c8_canvas_page", "c8_canvas_overlay"]}
				key="selectionOverlay"
			>
				{children}
			</div>
		);
	}
}

export default SelectionOverlay;
